import { Common, color } from '@stockbit/ui';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import If from 'global/ErrorHandlers/If';
import {
  STREAM_TABS,
  STREAM_TYPE,
  streamTypes as streamFilters,
} from 'global/StreamWidget/constants';
import Web from './Web';
import Mobile from './Mobile';
import Filter from './Filter';
import FilterReport from './FilterReportType';
import {
  NavigationContainer,
  NavigationTabContainer,
  StreamMenuContainer,
  SearchBoxContainer,
} from './styled';
import SearchAdvanced from './SearchAdvanced';

import streamWidget, { effects, selectors } from '../slice';
import DateSwitcher from './DateSwitcher';
import SelectReportType from './SelectReportType';
import NavigationTab from './NavigationTab';
import ResearchBox from './NavigationTab/ResearchBox';
import {
  DATA_CY_SEARCH_STREAM_MENU_FILTER,
  DATA_CY_SEARCH_STREAM_MENU,
} from './NavigationTab/__tests__/constant';
import { DATA_CY_STREAM_WIDGET_NAVIGATION } from './__tests__/constant';

const { Flex, IconWrapper, Iconmoon } = Common;

interface NavigationProps {
  streamType: string;
  hasDefaultTab: boolean;
  symbol?: string;
  isSelfStream?: boolean;
}

function Navigation({
  streamType = STREAM_TYPE.GENERIC,
  hasDefaultTab = false,
  symbol = null,
  isSelfStream = false,
}: NavigationProps) {
  const dispatch = useDispatch();

  const {
    filterParams: { keyword, type, category },
    tabHeader,
  } = useSelector(selectors);

  const [isSearchVisible, setSearchVisible] = useState(false);
  const [isNotified, setIsNotified] = useState(false);

  const [STREAM, _NEWS, RESEARCH] = STREAM_TABS;

  const mappedFilter = streamFilters.map((item) => item.key);
  const isReports = category === 'reports';
  const showSelectReport = isReports && streamType !== STREAM_TYPE.GENERIC;
  const showFilter = [...mappedFilter].includes(type) && !isReports;

  const handleSearch = (val) => {
    dispatch(streamWidget.actions.setFilterParam({ ...val }));
  };

  const handleSelectTab = (value: string) => {
    // @ts-ignore
    dispatch(streamWidget.actions.setTabHeader(value));
  };

  const handleGetResearchIndicator = () => {
    dispatch(effects.getResearchIndicator(symbol));
  };

  const removeNewIndicator = () => {
    setIsNotified(true);
  };

  const toggleSearch = () => {
    setSearchVisible((prev) => !prev);
  };

  useEffect(() => {
    handleGetResearchIndicator();
  }, []);

  useEffect(() => {
    if (!isSearchVisible) {
      // if closing search params and there's keyword, reset search
      if (keyword) {
        const data = {
          keyword: '',
          from: '',
          to: '',
        };
        // @ts-ignore
        dispatch(streamWidget.actions.setFilterParam({ ...data }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isSearchVisible]);

  return (
    <NavigationContainer
      streamType={streamType}
      category={category}
      data-cy={DATA_CY_STREAM_WIDGET_NAVIGATION}
    >
      <If condition={streamType === STREAM_TYPE.GENERIC}>
        <NavigationTabContainer>
          <NavigationTab
            selectedTab={tabHeader}
            setSelectedTab={handleSelectTab}
            persistTab={hasDefaultTab}
            isNotified={isNotified}
            removeNewIndicator={removeNewIndicator}
          />
          <Flex pl="4px">
            <IconWrapper onClick={toggleSearch}>
              <Iconmoon
                className="icon-avatar_search"
                color={isSearchVisible ? color.primary : undefined}
                data-cy={DATA_CY_SEARCH_STREAM_MENU_FILTER}
              />
            </IconWrapper>
          </Flex>
        </NavigationTabContainer>
      </If>
      <If condition={tabHeader === STREAM.value}>
        <StreamMenuContainer>
          <If condition={type !== 'trending'}>
            <>
              <Web
                streamType={streamType}
                hasDefaultTab={hasDefaultTab}
                isSelfStream={isSelfStream}
              />
              <Mobile
                streamType={streamType}
                hasDefaultTab={hasDefaultTab}
                isSelfStream={isSelfStream}
              />
            </>
          </If>
          <If condition={type === 'trending'}>
            <DateSwitcher />
          </If>
          <Flex>
            <If condition={streamType === STREAM_TYPE.GENERIC}>
              <If condition={showFilter && category === 'ideas'}>
                <Filter />
              </If>
              <If condition={isReports}>
                <FilterReport />
              </If>
            </If>

            <If condition={streamType !== STREAM_TYPE.GENERIC && category !== 'notes'}>
              <Flex pl="20px">
                <IconWrapper onClick={toggleSearch}>
                  <Iconmoon
                    className="icon-avatar_search"
                    data-cy={`${DATA_CY_SEARCH_STREAM_MENU}-${streamType}`}
                  />
                </IconWrapper>
              </Flex>
            </If>
          </Flex>
        </StreamMenuContainer>
      </If>
      <If condition={category !== 'notes'}>
        <SearchBoxContainer active={isSearchVisible}>
          <SearchAdvanced
            isActive={isSearchVisible}
            onSearch={handleSearch}
            tabHeaderLabel={tabHeader}
          />
        </SearchBoxContainer>
      </If>
      <SearchBoxContainer active={showSelectReport && !isSearchVisible}>
        <SelectReportType />
      </SearchBoxContainer>
      <If
        condition={
          tabHeader === RESEARCH.value && streamType === STREAM_TYPE.GENERIC
        }
      >
        <ResearchBox />
      </If>
    </NavigationContainer>
  );
}

export default Navigation;
